import { useEffect, useRef } from "react";
import { Capacitor } from "@capacitor/core";
import { AdMob, RewardAdPluginEvents } from "@capacitor-community/admob";
import { ADMOB_REWARDED_UNIT_ID, isRewardedAdConfigured } from "./config.ts";
import { useT } from "../i18n.ts";

/**
 * The "광고보기" reward stand-in on the match loss/draw screen. On native
 * Android with a rewarded unit configured, loads and shows a real AdMob
 * rewarded ad; `onReward` fires only if the player actually earns the reward
 * (the `Rewarded` event), separate from `onClose`, which fires once the ad
 * flow ends either way (watched or dismissed early) — a closed-without-
 * -earning ad must not grant the points. Elsewhere (web, or no unit
 * configured yet) this falls back to the old placeholder, which grants the
 * reward on close like before (no way to require an unskippable watch there).
 */
export function RewardedAd({
  onClose,
  onReward,
  continueLabel,
}: {
  onClose: () => void;
  onReward: () => void;
  continueLabel?: string;
}) {
  const t = useT();
  const showReal = Capacitor.isNativePlatform() && isRewardedAdConfigured();

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const onRewardRef = useRef(onReward);
  onRewardRef.current = onReward;

  useEffect(() => {
    if (!showReal) return;
    let cancelled = false;
    let earned = false;

    const rewardedSub = AdMob.addListener(RewardAdPluginEvents.Rewarded, () => {
      earned = true;
    });
    const dismissedSub = AdMob.addListener(RewardAdPluginEvents.Dismissed, () => {
      if (cancelled) return;
      if (earned) onRewardRef.current();
      onCloseRef.current();
    });
    const failSub = AdMob.addListener(RewardAdPluginEvents.FailedToShow, () => {
      if (!cancelled) onCloseRef.current();
    });

    (async () => {
      try {
        await AdMob.prepareRewardVideoAd({ adId: ADMOB_REWARDED_UNIT_ID });
        if (cancelled) return;
        await AdMob.showRewardVideoAd();
      } catch {
        // couldn't load/show — close without granting the reward
        if (!cancelled) onCloseRef.current();
      }
    })();

    return () => {
      cancelled = true;
      rewardedSub.then((h) => h.remove()).catch(() => {});
      dismissedSub.then((h) => h.remove()).catch(() => {});
      failSub.then((h) => h.remove()).catch(() => {});
    };
  }, [showReal]);

  if (showReal) return null; // real ad is a native full-screen overlay

  return (
    <div className="gz-interstitial">
      <div className="gz-interstitial-box">
        <span>{t("adArea")}</span>
      </div>
      <button
        type="button"
        className="gz-interstitial-close"
        onClick={() => {
          onReward();
          onClose();
        }}
      >
        {continueLabel ?? t("close")}
      </button>
    </div>
  );
}
